import type { App } from "vue";
import { reactive, computed } from "vue";

export type SnackbarColor = "error" | "warning" | "info" | "success";

interface SnackbarState {
  color: SnackbarColor
  multiline: boolean
  vertical: boolean
  messages: string[]
  timeout: number
  timeoutId: ReturnType<typeof setTimeout> | null
}

export interface SnackbarService extends SnackbarState {
  enabled: boolean
  error(message: string): void
  warning(message: string): void
  info(message: string): void
  success(message: string): void
  addMessage(color: SnackbarColor, message: string): void
  reset(): void
}

// Create a reactive snackbar state
const snackbarState: SnackbarState = reactive({
  color: "error" as SnackbarColor,
  multiline: true,
  vertical: true,
  messages: [] as string[],
  timeout: 6000,
  timeoutId: null as ReturnType<typeof setTimeout> | null,
});

// Create computed enabled property
const enabled = computed(() => snackbarState.messages.length > 0);

// Create methods
const snackbarMethods = {
  error(message: string): void {
    this.addMessage("error", message);
  },
  warning(message: string): void {
    this.addMessage("warning", message);
  },
  info(message: string): void {
    this.addMessage("info", message);
  },
  success(message: string): void {
    this.addMessage("success", message);
  },
  addMessage(color: SnackbarColor, message: string): void {
    // Reset if changing color types
    if (snackbarState.color !== color) {
      this.reset();
    }

    // Clear existing timeout
    if (snackbarState.timeoutId) {
      clearTimeout(snackbarState.timeoutId);
    }

    // Set new timeout
    snackbarState.timeoutId = setTimeout(() => {
      this.reset();
    }, snackbarState.timeout);

    // Update state
    snackbarState.messages.push(message);
    snackbarState.color = color;
  },
  reset(): void {
    if (snackbarState.timeoutId) {
      clearTimeout(snackbarState.timeoutId);
      snackbarState.timeoutId = null;
    }
    snackbarState.messages = [];
  },
};

// Create the complete service
const snackbarService = Object.assign(snackbarState, snackbarMethods, { enabled }) as unknown as SnackbarService;

const Snackbar = {
  install(app: App): void {
    app.config.globalProperties.$snackbar = snackbarService;
    app.provide("$snackbar", snackbarService);
  },
};

// Composable for use in setup() functions
export function useSnackbar(): SnackbarService {
  return snackbarService;
}

export default Snackbar;
export { snackbarService as snackbarInstance };
